"use client";
import type { GPOnboardingStepProps } from "./utils";

import { ImmutableLogo } from "@/components/icons/ImmutableLogo";
import { Button } from "@/components/ui/button";
import { Action, ControlType } from "@/context/AnalyticsProvider";
import { GamePageSection } from "@/context/AnalyticsProvider/gamePage";
import Image from "next/image";
import { useCallback, useMemo } from "react";
import { GlassButton } from "../GlassButton";

export const OnboardingHybrid = ({
  onNext,
  isVisible,
  track,
}: GPOnboardingStepProps) => {
  const randomEntriesLeft = useMemo(
    () => Math.floor(Math.random() * (50 - 5 + 1)) + 5,
    [],
  );

  const handleDontHaveSteamClick = useCallback(() => {
    track({
      action: Action.PRESSED,
      extras: {
        control: "get-started-no-steam",
        controlLabel: "Don't have Steam?",
        controlType: ControlType.LINK,
        section: GamePageSection.ONBOARDING_MODAL,
      },
    });

    onNext();
  }, [track, onNext]);

  const handleRulesClick = useCallback(() => {
    track({
      action: Action.PRESSED,
      extras: {
        control: "official-rules",
        controlLabel: "Official Rules",
        controlType: ControlType.LINK,
        section: GamePageSection.ONBOARDING_MODAL,
      },
    });

    window.open("https://www.immutable.com/legal/acceptable-use", "_blank");
  }, [track]);

  const handlePrivacyClick = useCallback(() => {
    track({
      action: Action.PRESSED,
      extras: {
        control: "data-privacy",
        controlLabel: "Data Privacy",
        controlType: ControlType.LINK,
        section: GamePageSection.ONBOARDING_MODAL,
      },
    });

    window.open("https://www.immutable.com/legal/privacy-policy", "_blank");
  }, [track]);

  return (
    <div className="relative h-full w-full overflow-hidden">
      {/* Background Layer */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0">
        <div className="absolute inset-0 bg-[#131313]" />
        <div className="absolute inset-0 overflow-hidden">
          <Image
            src="/images/games/8ball/8ball-bg2.webp"
            alt=""
            fill
            className="object-cover"
            style={{
              objectPosition: "60% center",
            }}
            unoptimized
          />
        </div>
        <div className="absolute inset-0 bg-black/80" />
      </div>

      {/* Imagery Layer - Responsive positioning */}
      <div className="absolute left-0 top-0 h-full w-full overflow-hidden">
        {/* Merged Header Image - Top background, logo, prize cube, and animation */}
        <div className="absolute left-0 top-[1.875rem] h-full w-full overflow-hidden">
          <Image
            src="/images/games/8ball/onboarding-header-hybrid.webp"
            alt=""
            width={416}
            height={465}
            className="mx-auto"
            unoptimized
            priority
          />
        </div>

        {/* Chips at Bottom - Full width, natural height */}
        <div className="absolute bottom-0 left-0 w-full">
          <div className="pointer-events-none relative w-full">
            <Image
              src="/images/games/8ball/onboarding-chips.webp"
              alt=""
              width={412}
              height={157}
              className="w-full h-auto object-contain object-bottom"
              unoptimized
            />
          </div>
        </div>
      </div>

      {/* UI Content Layer with Gradient Overlay - Responsive */}
      <div
        className="absolute inset-0 flex flex-col items-center justify-between bg-gradient-to-b from-transparent from-[75%] to-black/20 px-5 pt-[clamp(21.25rem,36vh,21.875rem)]
       pb-[clamp(2rem,8vh,7.5rem)]"
      >
        {/* Title - Responsive spacing */}
        <div className="relative flex w-full shrink-0 flex-col items-center gap-[clamp(1rem,4vh,1.5rem)]">
          {/* Title - Responsive */}
          <p className="relative shrink-0 text-center font-suisse text-[clamp(1.5rem,6vw,2rem)] font-bold leading-[clamp(2rem,6.5vw,2.5rem)] text-[#f7f7f7]">
            Get a Founder’s Pack &<br />
            chance to win $60K!
          </p>
        </div>

        {/* Buttons Section - Responsive */}
        <div className="relative flex w-full shrink-0 flex-col items-center gap-[clamp(1.5rem,4vh,2rem)]">
          {/* Glass Button - Sign in with Steam */}
          <div className="relative w-full max-w-[23.25rem] mt-[3.125rem]">
            <GlassButton
              onClick={() => onNext()}
              control="get-started"
              section={GamePageSection.ONBOARDING_MODAL}
              className="!h-[clamp(3.75rem,9vh,4.5rem)] w-full min-h-[3.75rem] max-h-[4.5rem] flex-col px-8 py-2.5"
              variant="cta"
            >
              <p className="text-base font-medium leading-6 text-foreground">
                Sign in with Steam to enter
              </p>
              <p className="text-xs leading-4 text-foreground">
                Only {randomEntriesLeft} entries left
              </p>
            </GlassButton>

            {/* Prize Cube Overlay on Button - Responsive positioning */}
            <div className="absolute left-1/2 top-0 size-[clamp(3.75rem,18vw,4.6875rem)] -translate-x-1/2 -translate-y-3/4 pointer-events-none">
              <div className="pointer-events-none absolute inset-0 overflow-hidden">
                <Image
                  src="/images/games/8ball/onboarding-prize-cube.webp"
                  alt=""
                  fill
                  className="object-contain"
                  unoptimized
                />
              </div>
            </div>
          </div>

          {/* Don't have Steam Button */}
          <Button
            onClick={handleDontHaveSteamClick}
            variant="ghost"
            className="h-9 rounded-[1.375rem] bg-transparent px-4 py-2 text-sm text-foreground"
          >
            Don't have Steam?
          </Button>
        </div>

        {/* Rules & Privacy */}
        <div className="flex shrink-0 flex-col items-center gap-3">
          <div className="flex items-center gap-1 text-sm text-muted-foreground">
            <ImmutableLogo className="size- mr-1" fill="white" />
            <button
              type="button"
              className="text-neutral-300 underline underline-offset-2 hover:text-white transition-colors"
              onClick={handleRulesClick}
            >
              Official Rules
            </button>
            <span>&</span>
            <button
              type="button"
              className="text-neutral-300 underline underline-offset-2 hover:text-white transition-colors"
              onClick={handlePrivacyClick}
            >
              Data Privacy
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
